<template>
  <div class="chat-widget-container">
    <!-- Chat Popup Widget -->
    <div v-if="isOpen" class="chat-popup" :class="{ 'expanded': isExpanded }">
      <!-- Chat Header -->
      <div class="chat-popup-header">
        <div class="chat-title" aria-label="AI Assistant">
          <span class="ai-mark"></span>
          <span>AI Assistant</span>
        </div>
        <div class="chat-controls">
          <button 
            @click="toggleExpanded" 
            class="expand-btn"
            :class="{ 'expanded': isExpanded }"
            :title="isExpanded ? 'Minimize' : 'Expand'"
          >
          </button>
          <button 
            @click="closeChat" 
            class="close-btn"
            title="Close"
          >
          </button>
        </div>
      </div>

      <!-- Chat Content -->
      <div class="chat-popup-content" :class="{ 'expanded': isExpanded }">
        <!-- Quota Display -->
        <div class="quota" role="status" aria-live="polite">
          <template v-if="remaining > 0">
            <span>{{ remaining }} free question{{ remaining === 1 ? '' : 's' }} left</span>
          </template>
          <template v-else>
            <span>Free limit reached — premium required for more support.</span>
            <router-link v-if="featuresStore.isPricingEnabled" to="/pricing" class="upgrade-link">See premium plans</router-link>
          </template>
        </div>

        <!-- Messages -->
        <div class="chat-messages" ref="chatMessages">
          <div v-for="(m, i) in messages" :key="i" :class="['msg', m.role]">
            <div class="bubble markdown-body" v-html="renderMarkdown(m.content)"></div>
          </div>
          
          <!-- Loading indicator for AI response -->
          <div v-if="isLoading" class="msg assistant">
            <div class="bubble loading-bubble">
              <span class="dots" aria-hidden="true"><span></span><span></span><span></span></span>
              <span class="loading-text">Working…</span>
            </div>
          </div>
        </div>

        <!-- Input Form -->
        <form class="chat-input-form" @submit.prevent="send">
          <input
            v-model="input" 
            type="text" 
            class="chat-input" 
            :placeholder="placeholder" 
            :disabled="isLoading || remaining === 0"
          />
          <button class="send-btn" :disabled="!input.trim() || isLoading || remaining === 0">
            <span v-if="isLoading">...</span>
            <span v-else>Send</span>
          </button>
        </form>

        <!-- Footer -->
        <div class="chat-popup-footer">
          <a
            href="https://chat.openai.com/"
            target="_blank"
            rel="noopener noreferrer"
            class="external-help"
            aria-label="Open ChatGPT in a new tab"
          >Ask ChatGPT if you have more questions →</a>
        </div>
      </div>
    </div>

    <!-- Floating Action Button -->
      <button 
      @click="openChat" 
      class="chat-fab" 
      :class="{ 'hidden': isOpen }"
      aria-label="Open AI Chat"
    >
      <span class="bot">AI</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useChatStore } from '../stores/chat'
import { useFeaturesStore } from '../stores/features'
import apiService from '../services/apiService'

const featuresStore = useFeaturesStore()

interface ChatMessage { 
  role: 'user' | 'assistant'
  content: string 
}
const chatStore = useChatStore()

// Chat state
const isOpen = ref(false)
const isExpanded = ref(false)
const messages = ref<ChatMessage[]>([])
const input = ref('')
const chatMessages = ref<HTMLDivElement | null>(null)
const isLoading = ref(false)
const maxFreeQuestions = 2
const questionCount = ref(0)
const remaining = ref(maxFreeQuestions)

const placeholder = 'Type your question about a drug or condition…'

// Function to render markdown content
const renderMarkdown = (content: string): string => {
  if (!content) return ''
  
  // Parse markdown to HTML
  const html = marked.parse(content) as string
  
  // Sanitize HTML to prevent XSS attacks
  const sanitized = DOMPurify.sanitize(html)
  
  return sanitized
}

const seedAssistant = () => {
  messages.value.push({ 
    role: 'assistant', 
    content: 'Hi — I’m your AI health assistant. Ask about medications, interactions, dosing units, schedules, or safety.' 
  })
}

const openChat = () => {
  isOpen.value = true
  if (messages.value.length === 0) {
    seedAssistant()
  }
  nextTick(() => {
    scrollToBottom()
  })
}

const closeChat = () => {
  isOpen.value = false
  isExpanded.value = false
  chatStore.closeChat()
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  nextTick(() => {
    scrollToBottom()
  })
}

const send = async () => {
  const text = input.value.trim()
  if (!text || isLoading.value) return
  
  // Only check remaining questions for authenticated users
  const token = localStorage.getItem('token')
  if (token && remaining.value === 0) {
    const pricingLink = featuresStore.isPricingEnabled ? '<a href="/pricing">View plans</a>' : ''
    messages.value.push({ 
      role: 'assistant', 
      content: `You've used your two free questions. For unlimited chat and priority AI support, please upgrade to a premium account. ${pricingLink}` 
    })
    scrollToBottom()
    return
  }
  
  // Add user message
  messages.value.push({ role: 'user', content: text })
  input.value = ''
  scrollToBottom()
  
  // Set loading state
  isLoading.value = true
  
  try {
    // Call backend API - use public endpoint if not authenticated, authenticated endpoint if logged in
    const token = localStorage.getItem('token')
    const endpoint = token ? '/chat/message' : '/chat/message/public'
    const response = await apiService.post(endpoint, { message: text }, { skipAuth: !token })
    
    if (response && response.response) {
      messages.value.push({ role: 'assistant', content: response.response })
      
      // Only track question count for authenticated users
      if (token) {
        questionCount.value += 1
        remaining.value = Math.max(0, maxFreeQuestions - questionCount.value)
        if (remaining.value === 0) {
          const pricingLink = featuresStore.isPricingEnabled ? '<a href="/pricing">Upgrade here</a>' : ''
          messages.value.push({ 
            role: 'assistant', 
            content: `Free chat limit reached. To continue, please consider a premium plan. ${pricingLink}` 
          })
        }
      }
    } else {
      messages.value.push({ 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      })
    }
  } catch (error) {
    console.error('Chat error:', error)
    
    // Handle authentication errors specifically
    if (error instanceof Error && error.message.includes('Authentication required')) {
      messages.value.push({ 
        role: 'assistant', 
        content: 'Please log in to use the AI chat feature. <a href="/auth">Click here to log in</a>.' 
      })
    } else {
      messages.value.push({ 
        role: 'assistant', 
        content: 'Sorry, I\'m having trouble connecting. Please try again later.' 
      })
    }
  } finally {
    isLoading.value = false
    scrollToBottom()
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (chatMessages.value) {
      chatMessages.value.scrollTop = chatMessages.value.scrollHeight
    }
  })
}

// Watch for chat store changes to open chat
watch(() => chatStore.shouldOpenChat, (shouldOpen) => {
  if (shouldOpen) {
    openChat()
    chatStore.resetOpenChat() // Reset the flag after opening
  }
})

// Listen for custom events to open AI assistant (fallback)
const handleOpenAIAssistant = () => {
  openChat()
}

onMounted(() => {
  document.addEventListener('openAIAssistant', handleOpenAIAssistant)
})

onUnmounted(() => {
  document.removeEventListener('openAIAssistant', handleOpenAIAssistant)
})
</script>

<style scoped>
@import 'https://cdnjs.cloudflare.com/ajax/libs/github-markdown-css/5.2.0/github-markdown.min.css';

.chat-widget-container {
  position: fixed;
  right: 1rem;
  bottom: 5.5rem;
  z-index: 11000;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.chat-widget-container.fade-out {
  opacity: 0;
  transform: scale(0.8);
  pointer-events: none;
}

/* Floating Action Button */
.chat-fab {
  width: 2.9rem;
  height: 2.9rem;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  background: linear-gradient(180deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%);
  color: #fff;
  box-shadow: 0 10px 24px rgba(0,0,0,0.35);
  pointer-events: auto;
  border: 1px solid rgba(148, 163, 184, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease;
}

.chat-fab:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(0,0,0,0.45);
  border-color: rgba(148, 163, 184, 0.35);
}

.chat-fab.hidden {
  opacity: 0;
  pointer-events: none;
}

.bot { 
  font-size: 1.25rem; 
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25)); 
}


/* Chat Popup Widget */
.chat-popup {
  position: fixed;
  right: 1rem;
  bottom: 5.5rem;
  width: 350px;
  height: 500px;
  background: linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(15,23,42,0.96) 100%);
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(148, 163, 184, 0.22);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-popup.expanded {
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  border-radius: 0;
  border: none;
}

/* Chat Header */
.chat-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.6);
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.chat-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #e8ecf5;
}
.ai-mark {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34,197,94,0.15);
}

.chat-controls {
  display: flex;
  gap: 0.5rem;
}

.expand-btn,
.close-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 8px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  background: rgba(59, 130, 246, 0.1);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  backdrop-filter: blur(10px);
}

.expand-btn:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(99, 102, 241, 0.3));
  border-color: rgba(59, 130, 246, 0.6);
  transform: scale(1.05);
}

.close-btn:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3));
  border-color: rgba(239, 68, 68, 0.6);
  transform: scale(1.05);
}

/* Expand button - Plus icon */
.expand-btn::before {
  content: '+';
  position: absolute;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1;
}

/* Expand button - Minus icon when expanded */
.expand-btn.expanded::before {
  content: '−';
  position: absolute;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1;
}

/* Close button - X icon */
.close-btn::before {
  content: '×';
  position: absolute;
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1;
}

.close-btn::after {
  display: none;
}

/* Chat Content */
.chat-popup-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-popup-content.expanded {
  padding: 0 2rem;
}

/* Quota Display */
.quota {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  background: rgba(30, 41, 59, 0.5);
}

.upgrade-link {
  color: #60a5fa;
  text-decoration: none;
  font-weight: 600;
}

.upgrade-link:hover {
  text-decoration: underline;
}

/* Messages */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.msg {
  display: flex;
}

.msg.user {
  justify-content: flex-end;
}

.msg.user .bubble {
  background: rgba(59, 130, 246, 0.16);
  border: 1px solid rgba(59, 130, 246, 0.28);
  color: var(--text-color);
}

.msg.assistant .bubble {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.18);
  color: var(--text-color);
}

.bubble {
  padding: 0.75rem 1rem;
  border-radius: 14px;
  max-width: 80%;
  line-height: 1.5;
  word-wrap: break-word;
}

.loading-bubble {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dots { display: inline-flex; gap: 4px; }
.dots span { width: 6px; height: 6px; border-radius: 50%; background: #93c5fd; opacity: 0.7; animation: bounce 1.2s infinite ease-in-out; }
.dots span:nth-child(2) { animation-delay: 0.15s; }
.dots span:nth-child(3) { animation-delay: 0.3s; }

.loading-text {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

/* Input Form */
.chat-input-form {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(30, 41, 59, 0.5);
  border-top: 1px solid rgba(59, 130, 246, 0.2);
}

.chat-input {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.8);
  color: var(--text-color);
  font-size: 0.9rem;
}

.chat-input:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 10px rgba(96, 165, 250, 0.2);
}

.chat-input::placeholder {
  color: var(--text-secondary);
}

.send-btn {
  padding: 0.75rem 1rem;
  background: #60a5fa;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  background: #3b82f6;
  transform: translateY(-1px);
}

.send-btn:disabled {
  background: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Footer */
.chat-popup-footer {
  padding: 0.75rem 1rem;
  text-align: center;
  background: rgba(30, 41, 59, 0.3);
  border-top: 1px solid rgba(59, 130, 246, 0.1);
}

.external-help {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.8rem;
}

.external-help:hover {
  color: #60a5fa;
  text-decoration: underline;
}

/* Animations */
@keyframes brainPulse {
  0% { transform: scale(0.6); opacity: 0.7; }
  70% { transform: scale(1.2); opacity: 0; }
  100% { transform: scale(1.2); opacity: 0; }
}

@keyframes bounce { 0%, 80%, 100% { transform: scale(0.8); opacity: 0.6; } 40% { transform: scale(1); opacity: 1; } }

/* Responsive Design */
@media (max-width: 768px) {
  .chat-widget-container {
    right: 0.5rem;
    bottom: 6.5rem;
  }
  
  .chat-popup {
    right: 0.5rem;
    bottom: 6.5rem;
    width: calc(100vw - 1rem);
    height: calc(100vh - 7rem);
  }
  
  .chat-popup.expanded {
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
  }
  
  .chat-popup-content.expanded {
    padding: 0 1rem;
  }
}
</style>


